import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, LayoutTemplate, LoaderCircle, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';

export const ImagePurposeAttachMenu = ({ disabled = false, busy = false, onPick }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const attachInputRef = useRef(null);
  const designInputRef = useRef(null);
  const isDisabled = disabled || busy;

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const handleFiles = (purpose) => (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) {
      onPick?.(files, purpose);
    }
    event.target.value = '';
    setOpen(false);
  };

  const items = [
    {
      key: 'attach',
      label: t('attachImage'),
      icon: ImageIcon,
      onClick: () => attachInputRef.current?.click()
    },
    {
      key: 'design',
      label: t('designFromUi'),
      icon: LayoutTemplate,
      onClick: () => designInputRef.current?.click()
    }
  ];

  return (
    <div ref={rootRef} className="relative">
      <input
        ref={attachInputRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={handleFiles('attach')}
        disabled={isDisabled}
      />
      <input
        ref={designInputRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={handleFiles('design')}
        disabled={isDisabled}
      />
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => setOpen((value) => !value)}
        aria-label={t('chatToolMore')}
        aria-expanded={open}
        className={classNames(
          'inline-flex h-9 w-9 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40',
          open
            ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-950'
            : 'text-neutral-500 hover:bg-neutral-200/70 hover:text-neutral-800 dark:hover:bg-neutral-800'
        )}
      >
        {busy ? <LoaderCircle size={18} className="animate-spin" /> : <Plus size={18} />}
      </button>
      {open ? (
        <div className="absolute bottom-full start-0 z-50 mb-2 min-w-[240px] overflow-hidden rounded-2xl border border-neutral-200 bg-[#F3F4F6] py-1 shadow-[0_12px_40px_rgba(0,0,0,0.08)] dark:border-neutral-800 dark:bg-neutral-900">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={item.onClick}
                className="flex w-full items-center gap-3 px-3.5 py-2.5 text-start text-sm text-neutral-700 transition hover:bg-white dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                <Icon size={16} className="shrink-0 opacity-80" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
